import { useState, useCallback } from 'react';

const STORAGE_KEY = 'urbanix-search-history';
const MAX_ENTRIES = 15;

/**
 * Custom hook for managing search history in localStorage.
 * Each entry: { address, gush, helka, municipality, searchMode, timestamp }
 */
export default function useSearchHistory() {
  const [history, setHistory] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const addEntry = useCallback((entry) => {
    setHistory((prev) => {
      // Deduplicate: remove any existing entry with same address or same gush+helka
      const filtered = prev.filter((item) => {
        if (entry.searchMode === 'address' && item.searchMode === 'address') {
          return item.address !== entry.address;
        }
        if (entry.gush && entry.helka && item.gush && item.helka) {
          return !(item.gush === entry.gush && item.helka === entry.helka);
        }
        return item.address !== entry.address;
      });

      const newHistory = [
        { ...entry, timestamp: Date.now() },
        ...filtered,
      ].slice(0, MAX_ENTRIES);

      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newHistory));
      } catch { /* quota exceeded — silently ignore */ }

      return newHistory;
    });
  }, []);

  const removeEntry = useCallback((timestamp) => {
    setHistory((prev) => {
      const newHistory = prev.filter((item) => item.timestamp !== timestamp);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newHistory));
      } catch { /* ignore */ }
      return newHistory;
    });
  }, []);

  const clearHistory = useCallback(() => {
    setHistory([]);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch { /* ignore */ }
  }, []);

  return { history, addEntry, removeEntry, clearHistory };
}
